import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { ILocationInformation } from "@app/interfaces/ILocationInformation";
import { IPlot } from "@app/interfaces/IPlot";
import { LocationInformationService } from "@app/services/location-information/location-information.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subscription } from "rxjs";
import { debounceTime, take } from "rxjs/operators";
import './location-information.component.scss';


@UntilDestroy()
@Component({
    selector: 'location-information',
    template: require('./location-information.component.html')
})
export class LocationInformationComponent implements OnInit, OnChanges {

    @Input() plot: IPlot;
    @Input() small: boolean = false;
    @Input() showGeofenceIcon: boolean;
    @Input() hideEllipsis: boolean = false;
    @Input() showAddressWithGeofence: boolean = false;
    @Input() latLng: google.maps.LatLng;
    @Input() locationInformation: ILocationInformation;

    isGeofence: boolean;
    label: string = "";

    plotLocationSubscription: Subscription;

    constructor(
        private readonly locationInformationService: LocationInformationService,
        private readonly translationService: TranslationService) { }

    ngOnInit(): void {
        this.setLabel();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.plotLocationSubscription?.unsubscribe();

        if (changes['plot'] || changes['locationInformation']) {
            this.setLabel();
        }

        if (changes['latLng']) {
            this.setLocationInformationFromLatLng();
        }
    }

    //--

    private async setLabel(): Promise<void> {
        this.label = this.translationService.t("LocationInformation.Updating");

        if (this.latLng && !this.locationInformation) {
            this.setLocationInformationFromLatLng();
            return;
        }

        if (!this.plot && !this.locationInformation) {
            this.label = "N/A";
            return;
        }

        const locationInformation = this.plot?.LocationInformation || this.locationInformation;

        if (this.plot?.Id && !locationInformation) {
            this.setPlotLocation();
            return;
        }

        if (!locationInformation) {
            this.label = "N/A";
            return;
        }

        if (locationInformation.GeofenceIds?.length) {
            this.label = await this.locationInformationService.getGeofenceNamesString(locationInformation, this.showAddressWithGeofence);
            this.isGeofence = true;
            return;
        }

        this.isGeofence = false;

        this.label = locationInformation.LocationDescription;
    }

    private setPlotLocation(): void {
        if (!this.plot) {
            this.setLabel();
            return;
        }

        this.plotLocationSubscription = this.locationInformationService.getPlotLocation(this.plot.Id).pipe(
            debounceTime(500),
            untilDestroyed(this))
            .subscribe({
                next: async location => {
                    this.plot.LocationInformation = location;
                    await this.setLabel();
                }
            })
    }

    private setLocationInformationFromLatLng(): void {
        if (!this.latLng || this.locationInformation) {
            return;
        }

        this.locationInformationService.getLocationInformationFromLatLng(this.latLng).pipe(take(1)).subscribe({
            next: async locationInformation => {
                this.locationInformation = locationInformation;
                await this.setLabel();
            }
        })
    }
}
